import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import axios from "axios";
import {
  ShoppingCart,
  PackageCheck,
  MapPin,
  Truck,
  Gift,
  Heart,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";

const DashboardHome = () => {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);

  const { cartCount } = useCart();
  const { wishlist, loaded: wishlistLoaded } = useWishlist(); // ✅ context

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    if (!loading && !user) {
      window.location.href = "/signin";
    }

    const fetchUserData = async () => {
      try {
        const config = {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        };

        const ordersRes = await axios.get(`${BASE_URL}/api/orders/my`, config);
        setOrders(ordersRes.data);

        const addressesRes = await axios.get(`${BASE_URL}/api/addresses`, config);
        setSavedAddresses(addressesRes.data);
      } catch (err) {
        console.error("Error loading dashboard data:", err.response?.data || err.message);
      }
    };

    fetchUserData();
  }, [user, BASE_URL, loading]);

  const totalOrders = orders.length;
  const inProgress = orders.filter(
    (order) => order.status === "Pending" || order.status === "Processing" || order.status === "In Transit"
  ).length;
  const delivered = orders.filter((order) => order.status === "Delivered").length;
  const addressCount = savedAddresses.length;
  const wishlistCount = wishlistLoaded ? wishlist.length : 0; // ✅ user-scoped

  const metrics = [
    {
      title: "Items in Cart",
      count: cartCount,
      icon: <ShoppingCart className="w-6 h-6 text-blue-600" />,
      bg: "bg-white",
    },
    {
      title: "Total Orders",
      count: totalOrders,
      icon: <PackageCheck className="w-6 h-6 text-green-600" />,
      bg: "bg-white",
    },
    {
      title: "Saved Addresses",
      count: addressCount,
      icon: <MapPin className="w-6 h-6 text-purple-600" />,
      bg: "bg-white",
    },
    {
      title: "Deliveries In Progress",
      count: inProgress,
      icon: <Truck className="w-6 h-6 text-yellow-600" />,
      bg: "bg-white",
    },
    {
      title: "Gifts Delivered",
      count: delivered,
      icon: <Gift className="w-6 h-6 text-pink-600" />,
      bg: "bg-white",
    },
    {
      title: "Wishlist Items",
      count: wishlistCount,
      icon: <Heart className="w-6 h-6 text-red-600" />,
      bg: "bg-white",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-blue-600 text-white p-6 rounded-lg shadow">
        <h2 className="text-2xl font-bold">
          Hello{user?.name ? `, ${user.name}` : ""}!
        </h2>
        <p className="mt-2 text-sm">
          Here’s your dashboard overview. Let’s make someone smile today 😊
        </p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {metrics.map((metric) => (
          <div
            key={metric.title}
            className={`p-6 rounded-xl border shadow hover:shadow-md transition duration-200 ${metric.bg}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-600">
                {metric.title}
              </span>
              {metric.icon}
            </div>
            <div className="text-3xl font-bold text-gray-800">
              {metric.count}
            </div>
          </div>
        ))}
      </div>

      {/* Tip Section */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
        <p className="text-sm text-yellow-800">
          💡 Tip: Build your perfect hamper with your favorite flowers and accessories — your saved selections make reordering a breeze!
        </p>
      </div>
    </div>
  );
};

export default DashboardHome;
